import PropTypes from 'prop-types';
import React, { Fragment } from 'react';
import classnames from 'classnames';
import { locationShape, routerShape } from 'react-router/lib/PropTypes';
import _ from 'lodash';
import withRouter from 'react-router/lib/withRouter';

import config from '../config';
import urls from '../urls';
import { RouterProvider } from '../helpers/useRouter';
import UserStore from '../stores/UserStore';
import OrderStore from '../stores/OrderStore';
import InAppStore from '../stores/InAppStore';
import WidgetAuthStore from '../stores/WidgetAuthStore';
import LayoutStore from '../stores/LayoutStore';
import ModalActions from '../actions/ModalActions';
import {
    GlobalModals as GlobalModalType,
    PointStages,
    SkipAutoLogoutRedirectRoutes,
    SmartcardPages,
    NATIVE_BOOKING_FLOW_APP_CHANNEL,
    WEBVIEW_BOOKING_FLOW_APP_CHANNEL,
    GATWICK_APP_CHANNEL,
    GREATNORTHERN_APP_CHANNEL,
    SOUTHERN_APP_CHANNEL,
    THAMESLINK_APP_CHANNEL,
    PromotionPages,
} from '../constants/AppConstants';
import AppActions from '../actions/AppActions';
import OrderActions from '../actions/OrderActions';
import CustomerActions from '../actions/CustomerActions';
import connectToStores from '../helpers/connectToStores';
import RailcardsStore from '../stores/RailcardsStore';
import User from '../records/User';
import Order from '../records/Order';
import WindowActions from '../actions/WindowActions';
import FeatureFlagStore from "../stores/FeatureFlagStore";
import tabSwitch from "../helpers/tabSwitch";

import BetaBanner from './general/BetaBanner';
import MessageBanner from './general/MessageBanner';
import MicrositeBanner from './general/MicrositeBanner';
import SkipToContentLink from './SkipToContentLink';
import Header from './Header/Header';
import Footer from './Footer';
import FooterPoweredBy from './FooterPoweredBy';
import GlobalModals from './GlobalModals';
import Page from './general/Page';
import LoadingLine from './general/LoadingLine';
import Notifications from './general/Notifications';
import FeatureFlag from './general/FeatureFlag';
import { ModalManagerProvider } from './general/ModalManager';
import { ModalCounterProvider } from './general/ModalCounter';
import Webchat from './webchat/Webchat';

function _stateFromStores () {
    return {
        accountRailcardsLoaded: RailcardsStore.getAccountRailcardsLoaded(),
        isInApp: InAppStore.isInApp(),
        isWidgetAuth: WidgetAuthStore.isWidgetAuth(),
        isEmbeddedLayout: LayoutStore.isEmbeddedLayout(),
        user: UserStore.getCurrent(),
        order: OrderStore.getCurrent(),
        warnAutoLogout: UserStore.shouldAutoLogoutWarn(),
        shouldAutoLogout: UserStore.shouldAutoLogout(),
        enableInAppTokenRefreshOnPageLoad: FeatureFlagStore.get('enableInAppTokenRefreshOnPageLoad'),
        externalRegistrationLink: FeatureFlagStore.get('externalRegistrationLink'),
        hasFooterPoweredByOn: FeatureFlagStore.get('hasFooterPoweredByOn'),
    };
}

@withRouter
@connectToStores([ UserStore, InAppStore, WidgetAuthStore, OrderStore, RailcardsStore, LayoutStore ], _stateFromStores)
class App extends React.Component {

    static propTypes = {
        router: routerShape,
        routes: PropTypes.any.isRequired,
        location: locationShape,
        children: PropTypes.any.isRequired,
        user: PropTypes.instanceOf(User).isRequired,
        isInApp: PropTypes.bool,
        isWidgetAuth: PropTypes.bool,
        isEmbeddedLayout: PropTypes.bool,
        order: PropTypes.instanceOf(Order).isRequired,
        externalRegistrationLink: PropTypes.bool,
        hasFooterPoweredByOn: PropTypes.bool,
        enableInAppTokenRefreshOnPageLoad: PropTypes.bool
    };

    static childContextTypes = {
        routes: PropTypes.any,
    };

    static defaultProps = {
        hasFooterPoweredByOn: false,
    };

    state = {
        modalCount: 0
    };

    getChildContext () {
        return {
            routes: this.props.routes
        };
    }

    UNSAFE_componentWillReceiveProps (nextProps) {
        const { location: oldLocation } = this.props;
        const { location: newLocation } = nextProps;

        if (!_.isEqual(oldLocation, newLocation)) {
            AppActions.routeChanged(newLocation);
        }

        this._checkUserActivity(nextProps);
    }

    componentDidMount() {
        if (this.props.externalRegistrationLink) {
            document.addEventListener(tabSwitch().visibilityChange, this.handleVisibilityChange, false);
        }
    }

    handleVisibilityChange = () => {
        if (!document[tabSwitch().hiddenTab] && !this.props.user.isLoggedIn()) {
            CustomerActions.checkUserCookie();
        }
    };

    UNSAFE_componentWillMount () {
        const { location, user, isInApp, isWidgetAuth, enableInAppTokenRefreshOnPageLoad } = this.props;

        WindowActions.startListeners();

        AppActions.routeChanged(location);

        if (location.query && location.query.modal) {
            ModalActions.open(location.query.modal.toUpperCase(), location.query);
        }

        if (isInApp) {
            const disableAppTracking = FeatureFlagStore.get('disableAppTracking');

            const isTravelCompanionApp = [
                NATIVE_BOOKING_FLOW_APP_CHANNEL,
                WEBVIEW_BOOKING_FLOW_APP_CHANNEL,
                GATWICK_APP_CHANNEL,
                GREATNORTHERN_APP_CHANNEL,
                SOUTHERN_APP_CHANNEL,
                THAMESLINK_APP_CHANNEL
            ].includes(InAppStore.getChannel());

            if (disableAppTracking && isTravelCompanionApp) {
                /*
                    For Travel Companion app only for now

                    Hide cookie consent pop-up
                    and disable all tracking within the app webviews
                */
                AppActions.disableTracking();
                document.cookie = "CookieConsent={stamp:%27Tke7RiU7rNx4t6IZP0T16n53jSFhdBKFHjPI3h0yITls09pHpOQwig==%27%2Cnecessary:true%2Cpreferences:false%2Cstatistics:false%2Cmarketing:false%2Cver:4%2Cutc:1664468272652%2Cregion:%27gb%27}";
            }

            document.body.classList.add('inapp');
            if (user.accessToken) {
                CustomerActions.refresh(user, enableInAppTokenRefreshOnPageLoad);
            }
        } else if (isWidgetAuth) {
            document.body.classList.add('widget-auth');
        } else if (user.isLoggedIn() && !user.get('customer').get('id')) {
            CustomerActions.fetchUser(user.authToken);
        }

        if (!isInApp) {
            document.addEventListener('click', AppActions.activityDetected);
            document.addEventListener('keypress', AppActions.activityDetected);
            document.addEventListener('mousemove', AppActions.activityDetected);
            document.addEventListener('touchstart', AppActions.activityDetected);

            this._checkUserActivity(this.props, {});
        }

        this._checkExpiredOrder();
    }

    componentWillUnmount () {
        WindowActions.stopListeners();

        if (this.props.externalRegistrationLink) {
            document.removeEventListener(tabSwitch().visibilityChange, this.handleVisibilityChange);
        }

        if (!this.props.isInApp) {
            document.removeEventListener('click', AppActions.activityDetected);
            document.removeEventListener('keypress', AppActions.activityDetected);
            document.removeEventListener('mousemove', AppActions.activityDetected);
            document.removeEventListener('touchstart', AppActions.activityDetected);

            clearInterval(this.interval);
        }
    }

    _checkUserActivity = ({ user, warnAutoLogout, shouldAutoLogout }, prevProps = this.props) => {
        if (shouldAutoLogout && !prevProps.shouldAutoLogout) {
            CustomerActions.logout(user).then(() => {
                if (SkipAutoLogoutRedirectRoutes.includes(urls.resolve(this.props.location.pathname).name)) {
                    return null;
                }
                return this.props.router.push(urls.location(PointStages.SEARCH));
            }).then(() => {
                ModalActions.open(GlobalModalType.LOGGED_OUT, {
                    onClose: () => ModalActions.close(GlobalModalType.LOGGED_OUT)
                });
            });
        } else if (warnAutoLogout && !prevProps.warnAutoLogout) {
            ModalActions.open(GlobalModalType.LOGOUT_WARNING, {
                onClose: () => ModalActions.close(GlobalModalType.LOGOUT_WARNING)
            });
        }
    };

    _getCurrentRoute () {
        return urls.resolve(this.props.location.pathname);
    }

    _checkExpiredOrder = () => {
        OrderActions.checkBasket();
        setTimeout(() => this._checkExpiredOrder(), 60 * 1000);
    };

    _handleModalCount = (modalCount) => {
        this.setState({modalCount});
    }

    render() {
        const { hasFooterPoweredByOn, isEmbeddedLayout, children, ...restProps } = this.props;
        const classes = classnames('content container-fluid', {
            'inapp': isEmbeddedLayout,
            'container-self-smartcard': children.props && children.props.route.name === SmartcardPages.SELF_REGISTERED,
        });

        const canAutoRefocus = !this.props.routes.some(
            route => route.shouldRefocusRouteContent && !route.shouldRefocusRouteContent(),
        );
        const hasModals = Boolean(this.state.modalCount);

        return (
            <RouterProvider router={this.props.router} location={this.props.location}>
                <ModalManagerProvider>
                    <ModalCounterProvider onChange={this._handleModalCount}>
                    <Page name={ `${this._getCurrentRoute().name}` }>
                        <div
                            className={classnames(
                                `brand-${config.get('brand')}`,
                                {'has-modals': hasModals},
                            )}
                            aria-hidden={hasModals ? 'true' : null}
                        >
                            <GlobalModals />
                            { !isEmbeddedLayout && <FeatureFlag name="enableBetaBanner" expires="disableBetaBannerDate"><BetaBanner /></FeatureFlag> }

                            { !isEmbeddedLayout && <MessageBanner /> }

                            { <FeatureFlag name="enableMicrositeBanner"><MicrositeBanner /></FeatureFlag> }

                            <div className="fixed-container">
                                { !isEmbeddedLayout && (
                                    <Fragment>
                                        <SkipToContentLink />
                                        <Header
                                            user={ this.props.user }
                                            order={ this.props.order }
                                            disableNav={ children.props.route.name === SmartcardPages.SELF_REGISTERED }
                                            disableLogin={ children.props.route.name === PromotionPages.TOC_FAMILY }
                                        />
                                    </Fragment>
                                ) }

                                <section
                                    id="route__content"
                                    className={classes}
                                    role="main"
                                    tabIndex={canAutoRefocus ? -1 : undefined}
                                >
                                    {React.cloneElement(children, restProps)}
                                </section>

                                { !isEmbeddedLayout && <Footer hasFooterPoweredByOn={ hasFooterPoweredByOn }/> }
                                { hasFooterPoweredByOn && <FooterPoweredBy /> }
                            </div>
                            <LoadingLine />
                            <Notifications />
                            <Webchat />
                        </div>
                    </Page>
                    </ModalCounterProvider>
                </ModalManagerProvider>
            </RouterProvider>
        );
    }

}

export default App;



// WEBPACK FOOTER //
// ./src/components/App.js